import { useState, useCallback, useEffect } from 'preact/hooks'
import { Seite } from '../app.jsx'
import { api } from '../lib/api.js'
import { useLaden } from '../lib/laden.js'
import { datumText, zahl } from '../lib/format.js'
import { Laden, Fehler } from '../components/Zustand.jsx'
import { setzeAbzeichen } from '../lib/abzeichen.js'

/** Karten ohne Themenordner liegen im Server-Modell unter `__root__`. */
function themenName(ordner) {
  return ordner === '__root__' ? 'Ohne Thema' : ordner
}

function themenPfad(ordner) {
  return `/lernen?prefix=${encodeURIComponent(ordner)}`
}

function Themenzeile({ thema }) {
  return (
    <li class="themenzeile">
      <a class="themenzeile__link" href={themenPfad(thema.folder)}>
        <span class="themenzeile__name">{themenName(thema.folder)}</span>
        <span class="themenzeile__meta meta">
          {thema.total} Karten
          {/* Bei beidseitigen Themen steht neben der Karte auch die
              Rückrichtung in der Warteschlange — sonst wäre die Fälligkeitszahl
              rechts größer als die Kartenzahl links, ohne dass man wüsste warum. */}
          {thema.umkehr ? ' · beide Richtungen' : null}
        </span>
        {thema.due > 0 ? (
          <span class="themenzeile__faellig zahl">{thema.due}</span>
        ) : (
          <span class="themenzeile__ruhe meta">ab {datumText(thema.next_due)}</span>
        )}
      </a>
    </li>
  )
}

/**
 * Das Archiv steht eingeklappt am Fuß der Liste.
 *
 * Abgeschlossene Themen — die Platzreife etwa — sollen den Blick auf das
 * Fällige nicht mehr teilen, aber auch nicht verschwinden: die Karten bleiben,
 * das Thema bleibt lernbar, und ein Knopf holt es zurück (DESIGN.md, Ruhe).
 */
function Archiv({ themen, aufZurueckholen, arbeitet }) {
  if (!themen.length) return null
  return (
    <details class="archiv">
      <summary class="archiv__titel meta">
        Archiv · <span class="zahl">{themen.length}</span>{' '}
        {themen.length === 1 ? 'Thema' : 'Themen'}
      </summary>
      <ul class="archiv__liste">
        {themen.map((thema) => (
          <li class="archiv__zeile" key={thema.folder}>
            <a class="archiv__name" href={themenPfad(thema.folder)}>
              {themenName(thema.folder)}
            </a>
            <span class="archiv__meta meta">{thema.total} Karten</span>
            <button
              type="button"
              class="knopf knopf--still knopf--klein"
              disabled={arbeitet}
              onClick={() => aufZurueckholen(thema.folder)}
            >
              Zurückholen
            </button>
          </li>
        ))}
      </ul>
    </details>
  )
}

function Levelzeile({ stand }) {
  return (
    <section class="level" aria-label="Fortschritt">
      <p class="level__text meta">
        Stufe {stand.level} · <span class="zahl">{zahl(stand.total_xp)}</span> XP
      </p>
      <div
        class="level__balken"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(stand.xp_progress_pct)}
        aria-label={`Fortschritt zu Stufe ${stand.level + 1}`}
      >
        <div class="level__fuellung" style={{ width: `${stand.xp_progress_pct}%` }} />
      </div>
    </section>
  )
}

export function Themen() {
  const { daten, laedt, fehler, neuLaden } = useLaden(
    () => Promise.all([api.get('/api/topics'), api.get('/api/gamification')]),
    [],
  )
  const [arbeitet, setArbeitet] = useState(false)
  const [meldung, setMeldung] = useState(null)

  // Die Startseite ist die einzige Stelle, die die Gesamtzahl kennt — und die,
  // auf der man nach einer Sitzung landet. useLaden lädt beim Rückkehren aus
  // dem Hintergrund selbst nach, damit zieht die Zahl am Symbol mit.
  const faellig = daten?.[0]?.due_total
  useEffect(() => {
    if (typeof faellig === 'number') setzeAbzeichen(faellig)
  }, [faellig])

  const zurueckholen = useCallback(
    (ordner) => {
      setArbeitet(true)
      setMeldung(null)
      api
        .post('/api/topics/archive', { folder: ordner, archived: false })
        .then(() => neuLaden())
        .catch((f) => setMeldung(f.message))
        .finally(() => setArbeitet(false))
    },
    [neuLaden],
  )

  if (laedt && !daten) {
    return (
      <Seite titel="Karteikarten">
        <Laden />
      </Seite>
    )
  }

  if (fehler) {
    return (
      <Seite titel="Karteikarten">
        <Fehler fehler={fehler} erneut={neuLaden} />
      </Seite>
    )
  }

  const [themen, stand] = daten
  const aktive = themen.topics.filter((t) => !t.archived)
  const archivierte = themen.topics.filter((t) => t.archived)

  return (
    <Seite titel="Karteikarten">
      {themen.due_total > 0 ? (
        <a class="knopf knopf--haupt knopf--breit themen__start" href="/lernen">
          {/* Ein Textblock: als einzelne Flex-Kinder bekämen die Teile den
              Knopf-Abstand und „360  fällig“ stünde auseinander. */}
          <span>
            Alle lernen · <span class="zahl">{themen.due_total}</span> fällig
          </span>
        </a>
      ) : (
        <p class="meta themen__ruhe">Heute ist nichts fällig.</p>
      )}

      {meldung ? (
        <p class="zustand zustand--fehler" role="alert">
          {meldung}
        </p>
      ) : null}

      <ul class="themenliste">
        {aktive.map((thema) => (
          <Themenzeile key={thema.folder} thema={thema} />
        ))}
      </ul>

      <Archiv themen={archivierte} aufZurueckholen={zurueckholen} arbeitet={arbeitet} />

      <div class={themen.due_total > 0 ? 'themen--mit-start' : undefined}>
        <Levelzeile stand={stand} />
      </div>
    </Seite>
  )
}
